import { isCancel } from 'axios';
import { Dispatch, MutableRefObject, SetStateAction, useRef, useState } from 'react';
import { toast } from 'shared/components';
import { getStateStatusIcon } from 'shared/utils/get-status-icon';
import { useLocoTranslation } from './use-loco-translation';
type ErrorStatusType<E> = E;
type LoadStatusType = null;
type SuccessStatusType = true;
type StatusType<E> = ErrorStatusType<E> | LoadStatusType | SuccessStatusType | undefined;
interface State<T, E> {
  timer: MutableRefObject<NodeJS.Timeout | undefined>;
  status: StatusType<E>;
  isInProgress: boolean;
  isSuccess: boolean;
  isCalm: boolean;
  isFailed: boolean;
  error?: ErrorStatusType<E>;
  setStatus: Dispatch<SetStateAction<StatusType<E>>>;
  prevValue: T | undefined;
  setPrevValue: Dispatch<SetStateAction<T | undefined>>;
  actualValue: T | undefined;
  setActualValue: Dispatch<SetStateAction<T | undefined>>;
}

/**
 * Builds the `isValueChanged` comparator for a field that is saved on blur. The hook only knows the
 * values this session saved, so on an untouched field it has nothing to compare against and the
 * first blur counts as a change — which fires a save of data the server already holds. Passing the
 * fetched value gives that first comparison its baseline.
 */
export const changedFromFetched = <T,>(fetchedValue: T) => (savedValue: T | undefined, newValue: T) => (savedValue === undefined ? fetchedValue : savedValue) !== newValue;
export const useActOnBlur = <T, E = string>(initializer: (state: State<T, E>) => {
  action: (value: T) => Promise<void>;
  onError?: (error: Error) => void;
  isValueChanged?: (a: T | undefined, b: T) => boolean;
}, skipAction?: boolean) => {
  const {
    t
  } = useLocoTranslation();
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);
  const evocationCount = useRef(0);
  const [actualValue, setActualValue] = useState<T>();
  const [processingValue, setProcessingValue] = useState<T>();
  const [status, setStatus] = useState<StatusType<E>>();
  const isInProgress = status === null;
  const isSuccess = status === true;
  const isCalm = status === undefined;
  const isFailed = !isInProgress && !isSuccess && !isCalm;
  const statusIcon = getStateStatusIcon(isInProgress, isSuccess);
  const error = isFailed ? status : undefined;
  const {
    action,
    onError,
    isValueChanged = (a, b) => a !== b
  } = initializer({
    timer,
    status,
    setStatus,
    prevValue: actualValue,
    setPrevValue: setActualValue,
    actualValue,
    setActualValue,
    isInProgress,
    isSuccess,
    isCalm,
    isFailed,
    error
  });
  const handleBlur = async (newValue: T) => {
    if (skipAction) return;
    let currentEvocationCount;
    try {
      if (!isValueChanged(isInProgress ? processingValue : actualValue, newValue)) return;
      setStatus(null);
      clearTimeout(timer.current);
      currentEvocationCount = ++evocationCount.current;
      setProcessingValue(newValue);
      await action(newValue);
      setActualValue(newValue);
      setStatus(true);
      setProcessingValue(undefined);
      timer.current = setTimeout(() => {
        setStatus(undefined);
      }, 5_000);
    } catch (e) {
      if (isCancel(e)) {
        // true means that the request was canceled by another handler, we nullify the state
        if (evocationCount.current === currentEvocationCount) {
          setStatus(undefined);
          setProcessingValue(undefined);
        }
        return;
      }
      setStatus(undefined);
      setProcessingValue(undefined);
      handleError(e as Error);
    }
  };
  function handleError(error_0: Error) {
    onError?.(error_0) || toast.error(error_0.message || t('global.error'));
  }
  return {
    isInProgress,
    isSuccess,
    isCalm,
    isFailed,
    error,
    status,
    statusIcon,
    setStatus,
    prevValue: actualValue,
    setPrevValue: setActualValue,
    actualValue,
    setActualValue,
    handleBlur,
    handleError
  };
};